import { AST } from '../../../ast';
import { Constraints } from '../../../constraint';

type Example = [eraql: string, expected: any];

type Doc = {
  description: string;
  examples: Example[];
  advanced?: boolean;
  typeConstraints: Constraints.TypeConstraint;
};
type Docs = Record<AST.FunctionIdentifier, Doc>;

export const BUILTTINS_DOCS: Partial<Docs> = {
  abs: {
    description: 'Find the absolute value of a float or int',
    examples: [
      ['abs(1)', 1],
      ['abs(-1)', 1],
      ['-5.5 |> abs()', 5.5],
      ['0 |> abs()', 0],
    ],
    typeConstraints: [Constraints.ty('int'), Constraints.ty('float')],
  },
  ln: {
    description: 'Calculate the natural logarithm (base e) of a number',
    examples: [
      ['ln(1)', 0],
      ['ln(2)', 0.6931471805599453],
      ['10 |> ln()', 2.302585092994046],
      ['100 |> ln()', 4.605170185988092],
    ],
    typeConstraints: [Constraints.ty('int'), Constraints.ty('float')],
  },
  log_2: {
    description: 'Calculate the logarithm of a number with base 2',
    examples: [
      ['log_2(1)', 0],
      ['log_2(2)', 1],
      ['8 |> log_2()', 3],
      ['256 |> log_2()', 8],
    ],
    typeConstraints: [Constraints.ty('int'), Constraints.ty('float')],
  },
  log_10: {
    description: 'Calculate the logarithm of a number with base 10',
    examples: [
      ['log_10(1)', 0],
      ['log_10(10)', 1],
      ['100 |> log_10()', 2],
      ['1000 |> log_10()', 3],
    ],
    typeConstraints: [Constraints.ty('int'), Constraints.ty('float')],
  },
  // percentile_cont: {
  //   description:
  //     'Calculate the continuous percentile of a dataset at a given fraction',
  //   examples: [
  //     ['percentile_cont([1, 2, 3, 4, 5], 0.5)', 3],
  //     ['percentile_cont([1, 2, 3, 4, 5], 0.25)', 2],
  //     ['percentile_cont([1, 2, 3, 4, 5], 0.75)', 4],
  //     ['percentile_cont([1, 2, 3, 4, 5, 6], 0.5)', 3.5],
  //   ],
  // },
  // percentile_disc: {
  //   description:
  //     'Calculate the discrete percentile of a dataset at a given fraction',
  //   examples: [
  //     ['percentile_disc([1, 2, 3, 4, 5], 0.5)', 3],
  //     ['percentile_disc([1, 2, 3, 4, 5], 0.25)', 2],
  //     ['percentile_disc([1, 2, 3, 4, 5], 0.75)', 4],
  //     ['percentile_disc([1, 2, 3, 4, 5, 6], 0.5)', 3],
  //   ],
  // },
  floor: {
    description: 'Round a number down to the nearest integer',
    examples: [
      ['floor(1.9)', 1],
      ['floor(1.1)', 1],
      ['-1.1 |> floor()', -2],
      ['5 |> floor()', 5],
    ],
    typeConstraints: [Constraints.ty('int'), Constraints.ty('float')],
  },
  ceil: {
    description: 'Round a number up to the nearest integer',
    examples: [
      ['ceil(1.1)', 2],
      ['ceil(1.9)', 2],
      ['-1.9 |> ceil()', -1],
      ['5 |> ceil()', 5],
    ],
    typeConstraints: [Constraints.ty('int'), Constraints.ty('float')],
  },
  cosine_distance: {
    description: 'Calculate the cosine distance between two vectors',
    examples: [
      ['cosine_distance([1, 0], [1, 0])', 0],
      ['cosine_distance([1, 0], [0, 1])', 1],
    ],
    typeConstraints: [Constraints.ty('int'), Constraints.ty('float')],
  },
  round: {
    description:
      'Round a number to the nearest integer or specified decimal places',
    examples: [
      ['round(1.5, 0)', 2],
      ['round(1.4, 0)', 1],
      ['1.56 |> round(1)', 1.6],
      ['-1.5 |> round(0)', -2],
    ],
    typeConstraints: [Constraints.ty('int'), Constraints.ty('float')],
  },
  is_numeric_string: {
    description: 'Check if a string can be interpreted as a number',
    examples: [
      [`is_numeric_string('123')`, true],
      // [`is_numeric_string('12.34')`, true],
      [`'abc' |> is_numeric_string()`, false],
      [`'123abc' |> is_numeric_string()`, false],
    ],
    typeConstraints: [Constraints.ty('string')],
  },
  lower: {
    description: 'Convert a string to lowercase',
    examples: [
      [`lower('HELLO')`, 'hello'],
      [`lower('Hello World')`, 'hello world'],
      [`'123' |> lower()`, '123'],
      [`'MIXED case' |> lower()`, 'mixed case'],
    ],
    typeConstraints: [Constraints.ty('string')],
  },
  upper: {
    description: 'Convert a string to uppercase',
    examples: [
      [`upper('hello')`, 'HELLO'],
      [`upper('Hello World')`, 'HELLO WORLD'],
      [`'123' |> upper()`, '123'],
      [`'mixed CASE' |> upper()`, 'MIXED CASE'],
    ],
    typeConstraints: [Constraints.ty('string')],
  },
  like: {
    description: 'Check if a string matches a pattern using SQL LIKE syntax',
    examples: [
      [`like('hello', 'h%')`, true],
      [`like('hello', '%lo')`, true],
      [`'hello' |> like('_ello')`, true],
      [`'hello' |> like('world')`, false],
    ],
    typeConstraints: [Constraints.ty('string'), Constraints.ty('string')],
  },
  format: {
    description: 'Format a string using templated strings',
    examples: [
      ['`hello {{1+1}}`', 'hello 2'],
      ['format`hello {{1+1}}`', 'hello 2'],
      ['`hello {{null_of(int)}}`', 'hello '],
    ],
    typeConstraints: [Constraints.ty('string')],
  },
  format_nullable: {
    description: 'Format a string, handling null values',
    examples: [
      ['format_nullable`hello {{1+1}}`', 'hello 2'],
      ['format_nullable`hello {{null_of(int)}}`', null],
    ],
    typeConstraints: [Constraints.ty('string')],
  },
  concat: {
    description: 'Concatenate two or more strings together',
    examples: [
      [`concat('Hello', ' ', 'World')`, 'Hello World'],
      [`concat('a', 'b', 'c')`, 'abc'],
      [`concat('prefix-', '123')`, 'prefix-123'],
      [`concat('', 'empty', '')`, 'empty'],
    ],
    typeConstraints: [Constraints.ty('string')],
  },
  length: {
    description: 'Get the length of a string',
    examples: [
      [`length('hello')`, 5],
      [`length('')`, 0],
      [`length(\`hello {{1+1}}\`)`, 7],
      [`length(format_nullable\`hello {{null_of(string)}}\`)`, null],
    ],
    typeConstraints: [Constraints.ty('string')],
  },
  replace: {
    description: 'Replace all occurrences of a substring with another string',
    examples: [
      [`replace('hello world', 'world', 'there')`, 'hello there'],
      [`replace('aaaa', 'a', 'b')`, 'bbbb'],
      [`'hello' |> replace('x', 'y')`, 'hello'],
      [`'one two three' |> replace(' ', '-')`, 'one-two-three'],
    ],
    typeConstraints: [Constraints.ty('string')],
  },
  split_part: {
    description: 'Split a string by a delimiter and return the specified part',
    examples: [
      [`split_part('hello-world', '-', 1)`, 'hello'],
      [`split_part('a,b,c', ',', 2)`, 'b'],
      [`'one.two.three' |> split_part('.', 3)`, 'three'],
      [`'a:b:c:d' |> split_part(':', 5)`, ''],
    ],
    typeConstraints: [Constraints.ty('string')],
  },
  starts_with: {
    description: 'Check if a string starts with a specified prefix',
    examples: [
      [`starts_with('hello world', 'hello')`, true],
      [`starts_with('hello world', 'world')`, false],
      [`'abc' |> starts_with('a')`, true],
      [`'' |> starts_with('')`, true],
    ],
    typeConstraints: [Constraints.ty('string')],
  },
  ends_with: {
    description: 'Check if a string ends with a specified suffix',
    examples: [
      [`ends_with('hello world', 'world')`, true],
      [`ends_with('hello world', 'hello')`, false],
      [`'abc' |> ends_with('c')`, true],
      [`'' |> ends_with('')`, true],
    ],
    typeConstraints: [Constraints.ty('string')],
  },
  remove_prefix: {
    description: 'Remove a prefix from a string if it exists',
    examples: [
      [`remove_prefix('prefix-value', 'prefix-')`, 'value'],
      [`remove_prefix('hello world', 'hello ')`, 'world'],
      [`'abc' |> remove_prefix('xyz')`, 'abc'],
      [`'aaa' |> remove_prefix('a')`, 'aa'],
    ],
    typeConstraints: [Constraints.ty('string')],
  },
  remove_suffix: {
    description: 'Remove a suffix from a string if it exists',
    examples: [
      [`remove_suffix('value-suffix', '-suffix')`, 'value'],
      [`remove_suffix('hello world', ' world')`, 'hello'],
      [`'abc' |> remove_suffix('xyz')`, 'abc'],
      [`'aaa' |> remove_suffix('a')`, 'aa'],
    ],
    typeConstraints: [Constraints.ty('string')],
  },
  category_concat: {
    description: 'Concatenate elements of a category array into a string',
    examples: [
      [`category_concat('a', 'b')`, 'a~>b'],
      [`category_concat(category_concat('a', 'b'), 'N/A')`, 'a~>b'],
      [`category_concat('apple', 'N/A')`, 'apple'],
      [`category_concat('N/A', 'N/A')`, 'N/A'],
    ],
    typeConstraints: [Constraints.ty('string')],
  },
  category_at: {
    description: 'Get an element at a specific index from a category array',
    examples: [
      [`category_at('a~>b~>c', 0)`, 'a'],
      [`category_at('a~>b~>c', 2)`, 'c'],
      [`category_at('a~>b~>c', 4)`, 'N/A'],
      [`category_at('N/A', 0)`, 'N/A'],
    ],
    typeConstraints: [Constraints.ty('string')],
  },
  substring: {
    description: 'Extract a substring from a string',
    examples: [
      [`substring('hello world', 1, 5)`, 'hello'],
      [`substring('hello world', 7, 100)`, 'world'],
      [`'hello' |> substring(2, 3)`, 'ell'],
      [`'hello' |> substring(100, 1000)`, ''],
    ],
    typeConstraints: [Constraints.ty('string')],
  },
  add: {
    description: 'Add two numbers together',
    examples: [
      ['add(1, 2)', 3],
      ['add(-1, 1)', 0],
      ['1.5 |> add(2.5)', 4],
      ['0 |> add(0)', 0],
    ],
    typeConstraints: Constraints.number(),
  },
  sub: {
    description: 'Subtract the second number from the first',
    examples: [
      ['sub(5, 2)', 3],
      ['sub(1, 1)', 0],
      ['10 |> sub(15)', -5],
      ['3.5 |> sub(1.5)', 2],
    ],
    typeConstraints: Constraints.number(),
  },
  mul: {
    description: 'Multiply two numbers together',
    examples: [
      ['mul(2, 3)', 6],
      ['mul(5, 0)', 0],
      ['-2 |> mul(4)', -8],
      ['1.5 |> mul(2)', 3],
    ],
    typeConstraints: Constraints.number(),
  },
  div: {
    description: 'Divide the first number by the second',
    examples: [
      ['div(6, 2)', 3],
      ['div(5, 2)', 2.5],
      ['1 |> div(0)', Infinity],
      ['0 |> div(5)', 0],
    ],
    typeConstraints: Constraints.number(),
  },
  to_the_power_of: {
    description: 'Raise a number to the power of another number',
    examples: [
      ['to_the_power_of(2, 3)', 8],
      ['to_the_power_of(10, 2)', 100],
      ['2 |> to_the_power_of(0)', 1],
      ['2 |> to_the_power_of(-1)', 0.5],
    ],
    typeConstraints: Constraints.number(),
  },
  and: {
    description: 'Logical AND operation on two boolean values',
    examples: [
      ['and(true, 2>1)', true],
      ['and(true, false)', false],
      ['false |> and(true)', false],
      ['2==1 |> and(false)', false],
    ],
    typeConstraints: [Constraints.ty('boolean')],
  },
  or: {
    description: 'Logical OR operation on two boolean values',
    examples: [
      ['or(2==1+1, true)', true],
      ['or(true, 1+1>3)', true],
      ['false |> or(true)', true],
      ['false |> or(false)', false],
    ],
    typeConstraints: [Constraints.ty('boolean')],
  },
  eq: {
    description: 'Check if two values are equal',
    examples: [
      ['eq(1, 1)', true],
      ['eq(5*5, 20+5)', true],
      ['1 |> eq(2)', false],
      ['null_of(int) |> eq(null_of(int))', null],
      ['5==5*1', true],
      // ['5==(8-3)', true],
    ],
    typeConstraints: 'any',
  },
  neq: {
    description: 'Check if two values are not equal',
    examples: [
      ['neq(1, 2)', true],
      [`neq('hello', 'world')`, true],
      ['1 |> neq(1)', false],
      [`null_of(string) |> neq('abcd')`, null],
      ['5!=2+3', false],
    ],
    typeConstraints: 'any',
  },
  gte: {
    description:
      'Check if the first value is greater than or equal to the second',
    examples: [
      ['gte(5, 3)', true],
      ['gte(5, 5)', true],
      ['3 |> gte(5)', false],
      [`'b' |> gte('a')`, true],
      ['5>=6', false],
      ['5>=5', true],
    ],
    typeConstraints: [...Constraints.number(), Constraints.ty('timestamp')],
  },
  gt: {
    description: 'Check if the first value is greater than the second',
    examples: [
      ['gt(5, 3)', true],
      ['gt(5, 5)', false],
      ['gt(3, 5)', false],
      [`gt('b', 'a')`, true],
      [`5>6`, false],
    ],
    typeConstraints: [Constraints.ty('boolean')],
  },
  lte: {
    description: 'Check if the first value is less than or equal to the second',
    examples: [
      ['lte(3, 5)', true],
      ['lte(5, 5)', true],
      ['lte(5, 3)', false],
      [`lte('a', 'b')`, true],
      ['3<=5', true],
      ['5<=5', true],
      ['5<=3', false],
    ],
    typeConstraints: [...Constraints.number(), Constraints.ty('timestamp')],
  },
  lt: {
    description: 'Check if the first value is less than the second',
    examples: [
      ['lt(3, 5)', true],
      ['lt(5, 5)', false],
      ['lt(5, 3)', false],
      [`lt('a', 'b')`, true],
      ['3<5', true],
      ['5<3', false],
    ],
    typeConstraints: [...Constraints.number(), Constraints.ty('timestamp')],
  },
  nan: {
    description: 'Represent a value that is not a number',
    examples: [
      ['nan()', NaN],
      ['is_nan(nan())', true],
      ['eq(nan(), nan())', false],
      ['is_nan(div(0, 0))', true],
    ],
    typeConstraints: Constraints.number(),
  },
  is_nan: {
    description: 'Check if a value is NaN (not a number)',
    examples: [
      ['is_nan(nan())', true],
      ['is_nan(1)', false],
      ['is_nan(div(0, 0))', true],
      [`is_nan(1/0)`, false],
    ],
    typeConstraints: Constraints.number(),
  },
  not: {
    description: 'Logical NOT operation on a boolean value',
    examples: [
      ['not(true)', false],
      ['not(false)', true],
      ['not(eq(1, 1))', false],
      ['not(lt(5, 3))', true],
    ],
    typeConstraints: 'any',
  },
  is_null: {
    description: 'Check if a value is null',
    examples: [
      ['is_null(null_of(int))', true],
      ['is_null(0)', false],
      [`is_null('')`, false],
      ['is_null(div(1, 0))', false],
    ],
    typeConstraints: 'any',
  },
  is_not_null: {
    description: 'Check if a value is not null',
    examples: [
      ['is_not_null(null_of(string))', false],
      ['is_not_null(0)', true],
      [`is_not_null('')`, true],
      ['is_not_null(div(1, 0))', true],
    ],
    typeConstraints: 'any',
  },
  gen_random_uuid: {
    description: 'Generate a random UUID',
    examples: [
      ['length(gen_random_uuid())', 36],
      ['eq(gen_random_uuid(), gen_random_uuid())', false],
    ],
    typeConstraints: 'any',
  },
  now: {
    description: 'Get the current timestamp',
    examples: [
      ['type_of(now())', 'timestamp!'],
      [`gt(now(), date_add(now(), -1, 'days'))`, true],
      [`date_diff(now(), now(), 'seconds')`, 0],
    ],
    typeConstraints: 'any',
  },
  random: {
    description: 'Generate a random number between 0 and 1',
    examples: [
      ['gte(random(), 0)', true],
      ['lt(random(), 1)', true],
      ['eq(random(), random())', false],
    ],
    typeConstraints: Constraints.number(),
  },
  null_if: {
    description:
      'Return null if the two values are equal, otherwise return the first value',
    examples: [
      ['null_if(1, 1)', null],
      ['null_if(1, 2)', 1],
      [`null_if('hello', 'hello')`, null],
      [`null_if('hello', 'world')`, 'hello'],
    ],
    typeConstraints: 'any',
  },
  coalesce: {
    description: 'Return the first non-null value from a list of values',
    examples: [
      ['coalesce(1, 2)', 1],
      ['coalesce(null_of(int), 3)', 3],
      ['coalesce(1, 3)', 1],
      ['coalesce(null_of(string), null_of(string))', null],
    ],
    typeConstraints: 'any',
  },
  impure: {
    description: 'Disables optimization and Constant folding',
    examples: [['impure(1+1)', 2]],
    advanced: true,
    typeConstraints: 'any',
  },
  one_of: {
    description: 'Check if a value is in a list of values',
    examples: [
      ['one_of(1, [1, 2, 3])', true],
      [`one_of('a', ['b', 'c'])`, false],
      // ['one_of(null_of(int), [1, 3])', null],
      [`one_of('hello', [])`, false],
    ],
    typeConstraints: 'any',
  },
  // md5: {
  //   description: 'Calculate the MD5 hash of a string',
  //   examples: [
  //     [`md5('hello')`, '5d41402abc4b2a76b9719d911017c592'],
  //     [`md5('')`, 'd41d8cd98f00b204e9800998ecf8427e'],
  //     [`length(md5('test'))`, 32],
  //     [`md5('a') != md5('b')`, true],
  //   ],
  // },
  // sum: {
  //   description: 'Calculate the sum of values in an array',
  //   examples: [
  //     ['sum([1, 2, 3])', 6],
  //     ['sum([10])', 10],
  //     ['sum([1.5, 2.5])', 4],
  //     ['sum([])', 0],
  //   ],
  // },
  count: {
    description: 'Count the number of elements in an array',
    examples: [
      [
        `
      Values([{ foo: 1 }, { foo: 2}])
      |> Summary({
        count: count(1),
      })
      `.trim(),
        [{ count: 2 }],
      ],
    ],
    typeConstraints: 'any',
  },
  // count_distinct: {
  //   description: 'Count the number of distinct elements in an array',
  //   examples: [
  //     ['count_distinct([1, 2, 2, 3])', 3],
  //     ['count_distinct([1, 1, 1])', 1],
  //     ['count_distinct([])', 0],
  //     ['count_distinct(["a", "b", "a"])', 2],
  //   ],
  // },
  // avg: {
  //   description: 'Calculate the average of values in an array',
  //   examples: [
  //     ['avg([1, 2, 3])', 2],
  //     ['avg([10])', 10],
  //     ['avg([1.5, 2.5])', 2],
  //     ['avg([])', null],
  //   ],
  // },
  // min: {
  //   description: 'Find the minimum value in an array',
  //   examples: [
  //     ['min([3, 1, 2])', 1],
  //     ['min([10])', 10],
  //     ['min([5.5, 3.3, 7.7])', 3.3],
  //     ['min([])', null],
  //   ],
  // },
  // max: {
  //   description: 'Find the maximum value in an array',
  //   examples: [
  //     ['max([3, 1, 2])', 3],
  //     ['max([10])', 10],
  //     ['max([5.5, 3.3, 7.7])', 7.7],
  //     ['max([])', null],
  //   ],
  // },
  // corr: {
  //   description: 'Calculate the correlation coefficient between two series',
  //   examples: [
  //     ['corr([1, 2, 3], [1, 2, 3])', 1],
  //     ['corr([1, 2, 3], [3, 2, 1])', -1],
  //     ['corr([1, 2, 3], [2, 2, 2])', 0],
  //     ['corr([], [])', null],
  //   ],
  // },
  // stddev_samp: {
  //   description:
  //     'Calculate the sample standard deviation of values in an array',
  //   examples: [
  //     ['stddev_samp([2, 4, 4, 4, 5, 5, 7, 9])', 2.138],
  //     ['stddev_samp([1, 1, 1])', 0],
  //     ['stddev_samp([10])', null],
  //     ['stddev_samp([])', null],
  //   ],
  // },
  // stddev_pop: {
  //   description:
  //     'Calculate the population standard deviation of values in an array',
  //   examples: [
  //     ['stddev_pop([2, 4, 4, 4, 5, 5, 7, 9])', 2],
  //     ['stddev_pop([1, 1, 1])', 0],
  //     ['stddev_pop([10])', 0],
  //     ['stddev_pop([])', null],
  //   ],
  // },
  // string_agg: {
  //   description:
  //     'Concatenate values in an array into a string with a delimiter',
  //   examples: [
  //     [`string_agg(['a', 'b', 'c'], ',')`, 'a,b,c'],
  //     [`string_agg(['a', 'b', 'c'], '-')`, 'a-b-c'],
  //     [`string_agg(['hello'], ',')`, 'hello'],
  //     [`string_agg([], ',')`, ''],
  //   ],
  // },
  // array_agg: {
  //   description: 'Aggregate values into an array',
  //   examples: [
  //     ['array_agg([1, 2, 3])', [1, 2, 3]],
  //     ['array_agg(["a", "b"])', ['a', 'b']],
  //     ['array_agg([10])', [10]],
  //     ['array_agg([])', []],
  //   ],
  // },
  // date_fmt: {
  //   description: 'Format a date or timestamp as a string',
  //   examples: [
  //     [`date_fmt(@2023-04-01, '%y-%m-%d')`, '2023-04-01'],
  //     // [`date_fmt('2023-04-01 14:30:00', 'HH:mm')`, '14:30'],
  //     // [`date_fmt('2023-04-01', 'MMM D, YYYY')`, 'Apr 1, 2023'],
  //     // [`date_fmt('2023-04-01', 'd')`, 'Sat'],
  //   ],
  // },
  date_diff: {
    description:
      'Calculate the difference between two dates in specified units',
    examples: [
      [`date_diff(@2023-04-01, @2023-04-15, 'days')`, 14],
      [`date_diff(@2023-01-01, @2023-02-01, 'seconds')`, 2678400],
      [`date_diff(@2023-01-01, @2024-01-01, 'years')`, 1],
    ],
    typeConstraints: [Constraints.ty('timestamp')],
  },
  date_add: {
    description: 'Add a specified amount of time to a date',
    examples: [
      [`date_add(@2023-04-01, 1, 'days')`, new Date('2023-04-02Z')],
      [`date_add(@2023-04-01, -1, 'days')`, new Date('2023-03-31Z')],
    ],
    typeConstraints: [Constraints.ty('timestamp')],
  },
  date_trunc: {
    description: 'Truncate a date to a specified precision',
    examples: [
      [`date_trunc(@2023-04-15, 'month')`, new Date('2023-04-01Z')],
      [`date_trunc(@2023-04-15, 'day')`, new Date('2023-04-15Z')],
      [`date_trunc(@2023-04-15, 'hour')`, new Date('2023-04-15Z')],
      [`date_trunc(@2023-04-15, 'year')`, new Date('2023-01-01Z')],
    ],
    typeConstraints: [Constraints.ty('timestamp')],
  },
  date_part: {
    description: 'Extract a part of a date as a number',
    examples: [
      [`date_part(@2023-04-15, 'month')`, 4],
      [`date_part(@2023-04-15, 'year')`, 2023],
      [`date_part(@2023-04-15, 'day')`, 15],
      [`date_part(@2023-04-15, 'hour')`, 0],
    ],
    typeConstraints: [Constraints.ty('timestamp')],
  },
  date_to_timestamp: {
    description:
      'Convert a date string (YYYY-MM-DD) to a timestamp by appending T00:00:00.000Z and casting',
    examples: [
      [`date_to_timestamp('2025-01-01')`, new Date('2025-01-01T00:00:00.000Z')],
      [`date_to_timestamp('2023-12-17')`, new Date('2023-12-17T00:00:00.000Z')],
    ],
    typeConstraints: [Constraints.ty('string')],
  },
  get_from_record: {
    description: 'Get a value from a record using a key',
    examples: [
      [`get_from_record({'a': 1, 'b': 2}, 'a')`, 1],
      [`get_from_record({'a': 1, 'b': 2}, 'b')`, 2],
      [`get_from_record({'a': 1, 'b': 2}, 'c')`, null],
      [`get_from_record({'b': 5}, 'a')`, null],
    ],
    typeConstraints: [Constraints.record('any')],
  },
  type_of: {
    description: 'Get the type of a value',
    examples: [
      [`type_of(1)`, 'RANGE(1, 1)!'],
      [`type_of('hello')`, `ENUM('hello')!`],
      [`type_of(true)`, 'boolean!'],
      [`type_of(null_of(int))`, 'int'],
    ],
    typeConstraints: 'any',
  },
  null_of: {
    description: 'Create a null value of a specified type',
    examples: [
      [`null_of('int')`, null],
      [`null_of('string')`, null],
      [`null_of('boolean')`, null],
      [`type_of(null_of('string'))`, 'string'],
    ],
    typeConstraints: 'any',
  },
  tag: {
    description: 'Tag a value with a type',
    examples: [
      [`tag(1, 'int')`, 1],
      [`tag('hello', 'string')`, 'hello'],
      [`type_of(tag(1, 'float'))`, 'RANGE(1, 1)! @float'],
    ],
    typeConstraints: 'any',
  },
  empty_array_of: {
    description: 'Create an empty array of a specified type',
    examples: [
      [`empty_array_of(int)`, []],
      [`empty_array_of(string)`, []],
      [`type_of(empty_array_of('int'))`, 'ARRAY(int)!'],
    ],
    typeConstraints: [Constraints.array('any')],
  },
};
